import { useState, useEffect } from "react";
import axios from "axios";
import { Loader2, Zap, CheckCircle2, X } from "lucide-react";
import { API } from "./constants";

export const WorkflowConfirmModal = ({ projectId, onConfirmed, onClose }) => {
  const [loading, setLoading] = useState(true);
  const [rec, setRec] = useState(null);
  const [selected, setSelected] = useState(null);
  const [confirming, setConfirming] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await axios.post(`${API}/projects/${projectId}/recommend-workflow`, {}, { withCredentials: true });
        setRec(data);
        setSelected(data.recommended.id);
      } catch (e) { console.error(e); }
      finally { setLoading(false); }
    })();
  }, [projectId]);

  const confirm = async () => {
    if (!selected || confirming) return;
    setConfirming(true);
    try {
      const { data } = await axios.post(`${API}/projects/${projectId}/confirm-workflow`, { workflow_id: selected }, { withCredentials: true });
      onConfirmed(data);
    } catch (e) { console.error(e); }
    finally { setConfirming(false); }
  };

  if (loading) return (
    <div className="fixed inset-0 z-50 bg-black/40 backdrop-blur-sm flex items-center justify-center" data-testid="workflow-modal-loading">
      <div className="bg-white rounded-2xl p-8 shadow-elevated"><Loader2 className="w-6 h-6 animate-spin text-[#014d7d]" /></div>
    </div>
  );

  return (
    <div className="fixed inset-0 z-50 bg-black/40 backdrop-blur-sm flex items-center justify-center p-4" data-testid="workflow-confirm-modal">
      <div className="bg-white rounded-2xl shadow-elevated w-full max-w-lg overflow-hidden" style={{ animation: "cardFadeIn 0.2s ease-out" }}>
        <div className="px-6 py-5 border-b border-border/40">
          <div className="flex items-center justify-between">
            <div>
              <h2 className="font-heading font-bold text-lg text-foreground">Select Workflow</h2>
              <p className="text-xs text-foreground/40 mt-1">Choose a workflow template for this project</p>
            </div>
            <button onClick={onClose} className="w-8 h-8 rounded-lg hover:bg-foreground/5 flex items-center justify-center" data-testid="close-workflow-modal"><X className="w-4 h-4 text-foreground/40" /></button>
          </div>
        </div>
        {rec && (
          <div className="px-6 py-3 bg-blue-50/50 border-b border-blue-100">
            <p className="text-xs text-[#014d7d]"><Zap className="w-3 h-3 inline mr-1" />{rec.reason}</p>
          </div>
        )}
        <div className="px-6 py-4 space-y-2 max-h-[340px] overflow-y-auto">
          {rec?.all_workflows?.map(wf => (
            <button
              key={wf.id} onClick={() => setSelected(wf.id)}
              className={`w-full text-left px-4 py-3.5 rounded-xl border-2 transition-all ${selected === wf.id ? "border-[#014d7d] bg-[#014d7d]/[0.03]" : "border-border/40 hover:border-foreground/20"}`}
              data-testid={`workflow-option-${wf.id}`}
            >
              <div className="flex items-center justify-between mb-1.5">
                <span className="text-sm font-semibold text-foreground">{wf.name}</span>
                {rec.recommended.id === wf.id && <span className="text-[9px] font-bold uppercase px-2 py-0.5 rounded-full bg-[#014d7d]/10 text-[#014d7d]">Recommended</span>}
              </div>
              <p className="text-[11px] text-foreground/40 mb-2">{wf.description}</p>
              <div className="flex items-center gap-1.5">
                {wf.stages?.map((s, i) => (
                  <span key={i} className="text-[9px] font-semibold px-2 py-0.5 rounded-md bg-foreground/[0.04] text-foreground/50">{s.name}</span>
                ))}
              </div>
            </button>
          ))}
        </div>
        <div className="px-6 py-4 border-t border-border/40 flex items-center justify-end gap-2">
          <button onClick={onClose} className="text-xs font-semibold px-4 py-2 rounded-lg text-foreground/50 hover:bg-foreground/5" data-testid="skip-workflow">Skip for now</button>
          <button onClick={confirm} disabled={!selected || confirming}
            className="text-xs font-semibold px-5 py-2 rounded-lg bg-[#014d7d] text-white disabled:opacity-40 flex items-center gap-1.5"
            data-testid="confirm-workflow-btn">
            {confirming ? <Loader2 className="w-3 h-3 animate-spin" /> : <CheckCircle2 className="w-3 h-3" />}
            Confirm & Apply
          </button>
        </div>
      </div>
    </div>
  );
};
